// Considered `SegmentedControlAtom` and `ButtonAtom` alone: neither holds the offset grammar
// (anchor ± amount unit) or the "which token is active" reading, so this molecule composes them —
// every pill row IS a `SegmentedControlAtom` (`variant="pills"`, button radiogroup), the
// disclosure and the apply action ARE `ButtonAtom`s; nothing here renders its own control.
import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import { SegmentedControlAtom, type SegmentedControlOption } from '../../atoms/SegmentedControl';
import {
  ROLLING_DATE_ANCHORS,
  ROLLING_DATE_KEY,
  ROLLING_DATE_QUICK_PRESETS,
  ROLLING_DATE_UNITS,
  buildRollingDateToken,
  humanizeParsedRollingDate,
  isRollingDateToken,
  quickPresetLabel,
  rollingDateAnchorLabel,
  rollingDateUnitLabel,
  type RollingDateAnchor,
  type RollingDateToken,
  type RollingDateUnit,
} from '../../../utils/rollingDateLabels';
import { parseRollingDateToken } from '@center-platform/spheres/filters';
import './RollingDatePresetsMolecule.scss';

export interface RollingDatePresetsMoleculeProps {
  /**
   * The value the date editor currently holds — a rolling-date token, a literal
   * date, or nothing. Only a token lights a preset; a literal date leaves the
   * whole row unpressed, which is what tells the author the calendar is in
   * charge.
   */
  value: unknown;
  /** Called with the chosen token. The caller writes it verbatim — never a resolved date. */
  onPick: (token: RollingDateToken) => void;
  readOnly?: boolean;
  /**
   * Parity instrumentation only — the `data-datum` the FIRST quick preset
   * carries, so the `@today` chip keeps the identity the prototype's
   * `EF_DATE_PRESETS` row gave it.
   */
  todayDatumId?: string;
  /** Forces the offset builder open. Omit to let the current value decide. */
  initialBuilderOpen?: boolean;
  className?: string;
}

const QUICK_PRESET_TOKENS = new Set<string>(ROLLING_DATE_QUICK_PRESETS.map((p) => p.token));

/** The draft the offset builder edits — the token's parts, with the amount still a string. */
interface BuilderDraft {
  anchor: RollingDateAnchor;
  sign: 1 | -1;
  amount: string;
  unit: RollingDateUnit;
}

const DEFAULT_DRAFT: BuilderDraft = { anchor: '@today', sign: -1, amount: '7', unit: 'd' };

/**
 * Seeds the builder from the value the editor holds, so opening it on an
 * existing rolling date shows that date rather than a blank form. A bare anchor
 * keeps the default amount/unit: the author has something to adjust instead of
 * a zero they must first replace.
 */
function seedDraft(value: unknown): BuilderDraft {
  const parsed = parseRollingDateToken(value);
  if (!parsed) return DEFAULT_DRAFT;
  if (parsed.unit === null) return { ...DEFAULT_DRAFT, anchor: parsed.anchor };
  return {
    anchor: parsed.anchor,
    sign: parsed.sign,
    amount: String(parsed.amount),
    unit: parsed.unit,
  };
}

/** The amount as the grammar's `0 | [1-9]\d{0,3}`, or `0` for anything it does not accept. */
function draftAmount(raw: string): number {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0) return 0;
  return Math.min(n, 9999);
}

/**
 * The relative-date affordance the date editors lead with, ahead of the
 * calendar (`context/filter-row.jsx:192` `EF_DATE_PRESETS`).
 *
 * ## Why a preset row plus a builder, and not a token text field
 *
 * A rolling date is a token with a grammar, and the grammar's whole point is
 * that a saved filter keeps meaning the same thing tomorrow. An author cannot
 * be asked to know the spelling: `@today-07d` is invalid, `@startOfMonth-1m` is
 * the start of LAST month rather than a month before today, and neither fact is
 * discoverable from a text box — a typo would simply be saved as a literal that
 * matches nothing. So the token is never typed. The six quick presets cover what
 * almost every filter wants in one click, and the builder composes any other
 * token from four closed choices, with a live reading of what it will mean. Both
 * emit through `buildRollingDateToken`, which can only produce strings the
 * grammar accepts.
 *
 * Every control is a plain toggle button in a labelled group — no overlay, no
 * listbox — so the whole affordance is reachable with Tab and Space, and the
 * chosen state is carried by `aria-pressed` rather than by colour alone.
 */
export const RollingDatePresetsMolecule: React.FC<RollingDatePresetsMoleculeProps> = ({
  value,
  onPick,
  readOnly = false,
  todayDatumId,
  initialBuilderOpen,
  className = '',
}) => {
  const { t } = useTranslation();
  const baseId = useId();
  const builderId = `${baseId}-builder`;

  const activeToken = isRollingDateToken(value) ? (value as RollingDateToken) : null;
  const isCustomToken = activeToken !== null && !QUICK_PRESET_TOKENS.has(activeToken);
  // `parseRollingDateToken` can return null for a token this build's grammar no longer accepts
  // (an older saved filter) — the toggle then falls back to the raw token text instead of a
  // non-null assertion masking a real parse failure.
  const activeParsedToken = activeToken ? parseRollingDateToken(activeToken) : null;

  // `null` = "nobody has decided" — the builder then follows the value, so a
  // custom token is always visible and editable rather than hidden behind a
  // disclosure that gives no hint it holds the current answer.
  const [openOverride, setOpenOverride] = useState<boolean | null>(
    initialBuilderOpen === undefined ? null : initialBuilderOpen
  );
  const showBuilder = openOverride ?? isCustomToken;

  const [draft, setDraft] = useState<BuilderDraft>(() => seedDraft(value));
  useEffect(() => {
    setDraft(seedDraft(activeToken));
  }, [activeToken]);

  const draftToken = useMemo(
    () => buildRollingDateToken(draft.anchor, draft.sign, draftAmount(draft.amount), draft.unit),
    [draft]
  );
  const draftReading = useMemo(() => {
    const parsed = parseRollingDateToken(draftToken);
    return parsed ? humanizeParsedRollingDate(parsed, t) : draftToken;
  }, [draftToken, t]);

  /**
   * One pill row. A single-select radiogroup of buttons: the selected option is the one tab
   * stop, arrow keys move it, and — for the quick row while a literal date is in charge —
   * nothing at all is selected, which the atom handles by keeping the first option reachable.
   */
  const pillRow = <T extends string>(
    idSuffix: string,
    options: SegmentedControlOption<T>[],
    value: T | '',
    onChange: (next: T) => void,
    extra?: { ariaLabel?: string; ariaLabelledBy?: string; datum?: string }
  ) => (
    <SegmentedControlAtom<T>
      mode="single"
      variant="pills"
      size="small"
      renderAs="button"
      optionKind="button"
      options={options.map((option) => ({ ...option, disabled: readOnly || option.disabled }))}
      value={value as T}
      onChange={(next) => onChange(next as T)}
      ariaLabel={extra?.ariaLabel}
      ariaLabelledBy={extra?.ariaLabelledBy}
      className={`rolling-date-presets__pills rolling-date-presets__pills--${idSuffix}`}
      data-testid={`rolling-date-presets-${idSuffix}`}
    />
  );

  const labelledGroup = <T extends string>(
    idSuffix: string,
    label: string,
    options: SegmentedControlOption<T>[],
    value: T,
    onChange: (next: T) => void
  ): React.ReactNode => {
    const labelId = `${baseId}-${idSuffix}`;
    return (
      <div className="rolling-date-presets__field">
        <span className="rolling-date-presets__field-label" id={labelId}>
          {label}
        </span>
        {pillRow(idSuffix, options, value, onChange, { ariaLabelledBy: labelId })}
      </div>
    );
  };

  const amountId = `${baseId}-amount`;

  return (
    <div
      className={`rolling-date-presets ${className}`.trim()}
      role="group"
      aria-label={t(`${ROLLING_DATE_KEY}.groupLabel`)}
      data-testid="rolling-date-presets"
    >
      <div className="rolling-date-presets__quick" data-container="each(EF_DATE_PRESETS)">
        {pillRow(
          'quick',
          ROLLING_DATE_QUICK_PRESETS.map((preset, index) => ({
            value: preset.token,
            label: quickPresetLabel(preset, t),
            datum: index === 0 ? todayDatumId : undefined,
            testId: `rolling-date-preset-${preset.token}`,
          })),
          activeToken && QUICK_PRESET_TOKENS.has(activeToken) ? activeToken : '',
          onPick,
          { ariaLabel: t(`${ROLLING_DATE_KEY}.quickLabel`) }
        )}
        <ButtonAtom
          type="button"
          variant="secondary"
          size="small"
          className={`rolling-date-presets__toggle${
            isCustomToken ? ' rolling-date-presets__toggle--active' : ''
          }`}
          onClick={() => setOpenOverride(!showBuilder)}
          disabled={readOnly}
          aria-expanded={showBuilder}
          aria-controls={builderId}
          aria-label={t(`${ROLLING_DATE_KEY}.builder.expandLabel`)}
          data-testid="rolling-date-preset-builder-toggle"
          label={
            isCustomToken && activeToken && activeParsedToken
              ? (humanizeParsedRollingDate(activeParsedToken, t) ?? activeToken)
              : isCustomToken && activeToken
                ? activeToken
                : t(`${ROLLING_DATE_KEY}.builder.toggle`)
          }
        />
      </div>

      {showBuilder && (
        <div className="rolling-date-presets__builder" id={builderId}>
          {labelledGroup(
            'anchor',
            t(`${ROLLING_DATE_KEY}.builder.anchorLabel`),
            ROLLING_DATE_ANCHORS.map((anchor) => ({
              value: anchor,
              label: rollingDateAnchorLabel(anchor, t),
              testId: `rolling-date-preset-anchor-${anchor.slice(1)}`,
            })),
            draft.anchor,
            (anchor) => setDraft((d) => ({ ...d, anchor }))
          )}

          <div className="rolling-date-presets__field">
            <label className="rolling-date-presets__field-label" htmlFor={amountId}>
              {t(`${ROLLING_DATE_KEY}.builder.amountLabel`)}
            </label>
            <InputAtom
              id={amountId}
              className="rolling-date-presets__amount"
              type="number"
              min={0}
              max={9999}
              step={1}
              value={draft.amount}
              disabled={readOnly}
              data-testid="rolling-date-preset-amount"
              onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
            />
          </div>

          {labelledGroup(
            'unit',
            t(`${ROLLING_DATE_KEY}.builder.unitLabel`),
            ROLLING_DATE_UNITS.map((unit) => ({
              value: unit,
              label: rollingDateUnitLabel(unit, t),
              testId: `rolling-date-preset-unit-${unit}`,
            })),
            draft.unit,
            (unit) => setDraft((d) => ({ ...d, unit }))
          )}

          {labelledGroup(
            'direction',
            t(`${ROLLING_DATE_KEY}.builder.directionLabel`),
            (['before', 'after'] as const).map((direction) => ({
              value: direction,
              label: t(`${ROLLING_DATE_KEY}.builder.${direction}`),
              testId: `rolling-date-preset-direction-${direction}`,
            })),
            draft.sign === -1 ? 'before' : 'after',
            (direction) => setDraft((d) => ({ ...d, sign: direction === 'before' ? -1 : 1 }))
          )}

          <p className="rolling-date-presets__reading" aria-live="polite">
            {t(`${ROLLING_DATE_KEY}.builder.readsAs`, { text: draftReading })}
          </p>

          <ButtonAtom
            type="button"
            variant="secondary"
            size="small"
            onClick={() => onPick(draftToken)}
            disabled={readOnly}
            data-testid="rolling-date-preset-apply"
            label={t(`${ROLLING_DATE_KEY}.builder.apply`)}
          />
        </div>
      )}
    </div>
  );
};

export default RollingDatePresetsMolecule;
